import { useCallback, useState } from "react";
import Cropper, { type Area } from "react-easy-crop";
import { X } from "react-feather";
import { toast } from "react-hot-toast";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { storageService } from "@/services/storage.service";
import { brandService } from "@/services/brand.service";
import type { BannerCrop, BannerCropArea } from "@/types/brand";
import {
  BANNER_DESKTOP_RATIO,
  BANNER_MOBILE_RATIO,
  renderBannerStrips,
} from "@/utils/bannerImage";

type Side = "desktop" | "mobile";

const SIDES: { key: Side; label: string; ratio: number; hint: string }[] = [
  {
    key: "desktop",
    label: "Desktop · 5:1",
    ratio: BANNER_DESKTOP_RATIO,
    hint: "The wide strip across the top of the join page on laptops and larger screens.",
  },
  {
    key: "mobile",
    label: "Mobile · 2:1",
    ratio: BANNER_MOBILE_RATIO,
    hint: "A taller cut for phones. Starts from the same centre as the desktop strip.",
  },
];

interface SideState {
  crop: { x: number; y: number };
  zoom: number;
  /** Percent area from react-easy-crop; undefined until the cropper reports one. */
  area?: BannerCropArea;
}

interface BannerFramingModalProps {
  /** The uploaded original (falls back to the desktop strip for old brands). */
  imageSrc: string;
  initialCrop?: BannerCrop | null;
  onClose: () => void;
  /** Called after the new strips are saved to the brand. */
  onSaved: () => Promise<void> | void;
}

/**
 * Non-destructive framing of the uploaded banner into the templated join
 * page's two strips. Both sides are re-rendered from the original on save so
 * the stored files always match the stored areas; the original itself is
 * never touched.
 */
export function BannerFramingModal({
  imageSrc,
  initialCrop,
  onClose,
  onSaved,
}: BannerFramingModalProps) {
  const [side, setSide] = useState<Side>("desktop");
  const [sides, setSides] = useState<Record<Side, SideState>>({
    desktop: { crop: { x: 0, y: 0 }, zoom: 1, area: initialCrop?.desktop },
    mobile: { crop: { x: 0, y: 0 }, zoom: 1, area: initialCrop?.mobile },
  });
  const [saving, setSaving] = useState(false);

  const current = sides[side];
  const meta = SIDES.find((s) => s.key === side)!;

  const update = useCallback(
    (key: Side, patch: Partial<SideState>) =>
      setSides((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } })),
    []
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const strips = await renderBannerStrips(imageSrc, "banner", {
        desktop: sides.desktop.area,
        mobile: sides.mobile.area,
      });
      const [brandBannerImage, brandBannerImageMobile] = await Promise.all([
        storageService.uploadCompanyBanner(strips.desktop),
        storageService.uploadCompanyBannerMobile(strips.mobile),
      ]);
      await brandService.updateBannerFraming({
        brandBannerImage,
        brandBannerImageMobile,
        brandBannerCrop: strips.crop,
      });
      await onSaved();
      toast.success("Banner framing saved");
      onClose();
    } catch (error) {
      console.error("Banner framing failed:", error);
      toast.error("Couldn't save the banner framing. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Banner framing</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Drag to reposition and zoom to choose what shows on each screen
              size. Your uploaded image stays as it is.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="px-6 pt-4 flex flex-wrap items-center justify-between gap-3">
          <Tabs value={side} onValueChange={(v) => setSide(v as Side)}>
            <TabsList>
              {SIDES.map((s) => (
                <TabsTrigger key={s.key} value={s.key}>
                  {s.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <p className="text-xs text-muted-foreground">{meta.hint}</p>
        </div>

        {/* Keyed by side so each tab mounts its own cropper with its own
            saved area; the previous side's area is kept in state. */}
        <div className="flex-1 relative min-h-[360px] bg-gray-900 mx-6 mt-4 rounded-lg overflow-hidden">
          <Cropper
            key={side}
            image={imageSrc}
            crop={current.crop}
            zoom={current.zoom}
            aspect={meta.ratio}
            showGrid
            restrictPosition
            initialCroppedAreaPercentages={current.area as Area | undefined}
            mediaProps={{ crossOrigin: "anonymous" }}
            onCropChange={(crop) => update(side, { crop })}
            onZoomChange={(zoom) => update(side, { zoom })}
            onCropComplete={(area) => update(side, { area })}
          />
        </div>

        <div className="p-6 space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-muted-foreground">Zoom</label>
              <span className="text-sm text-muted-foreground">
                {current.zoom.toFixed(1)}x
              </span>
            </div>
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={current.zoom}
              onChange={(e) => update(side, { zoom: Number(e.target.value) })}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-brand-600"
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save framing"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
